// Support ticket server functions.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { sendEmail } from "@/lib/email/send.server";
import { isPlatformAdmin } from "@/lib/admin-guards.server";

const CATEGORIES = [
  "integration_request",
  "question",
  "bug_report",
  "feedback",
  "billing",
  "other",
] as const;

const STATUSES = ["open", "in_progress", "waiting_on_user", "resolved", "closed"] as const;

async function notifyAdmins(payload: {
  isReply: boolean;
  subject: string;
  category: string;
  body: string;
  userEmail: string;
  ticketId: string;
}) {
  const { data: admins } = await supabaseAdmin
    .from("profiles")
    .select("id,email")
    .eq("is_platform_admin", true);
  if (!admins || admins.length === 0) return;
  const ticketUrl = `https://superaffiliateos.com/admin/support?ticket=${payload.ticketId}`;
  await Promise.all(
    admins
      .filter((a) => a.email)
      .map((a) =>
        sendEmail({
          templateName: "support_ticket_notification",
          recipientEmail: a.email as string,
          recipientUserId: a.id,
          templateData: { ...payload, ticketUrl },
          idempotencyKey: `support:${payload.ticketId}:${payload.isReply ? "reply" : "new"}:${Date.now()}`,
        }).catch((e) => console.error("[support] notify admin failed", e)),
      ),
  );
}

export const createSupportTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        category: z.enum(CATEGORIES),
        subject: z.string().trim().min(3).max(200),
        body: z.string().trim().min(5).max(5000),
        accountId: z.string().uuid().optional().nullable(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const userId = context.userId;
    const { data: ticket, error } = await supabaseAdmin
      .from("support_tickets")
      .insert({
        user_id: userId,
        account_id: data.accountId ?? null,
        category: data.category,
        subject: data.subject,
      })
      .select("id")
      .single();
    if (error || !ticket) throw new Error(error?.message ?? "Failed to create ticket");

    await supabaseAdmin.from("support_ticket_messages").insert({
      ticket_id: ticket.id,
      sender_user_id: userId,
      is_admin_reply: false,
      body: data.body,
    });

    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("email")
      .eq("id", userId)
      .maybeSingle();

    await notifyAdmins({
      isReply: false,
      subject: data.subject,
      category: data.category,
      body: data.body,
      userEmail: profile?.email ?? "(unknown)",
      ticketId: ticket.id,
    });

    return { id: ticket.id };
  });

export const listMySupportTickets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await supabaseAdmin
      .from("support_tickets")
      .select("id,subject,category,status,created_at,last_message_at,last_message_by_admin")
      .eq("user_id", context.userId)
      .order("last_message_at", { ascending: false });
    return { tickets: data ?? [] };
  });

export const getSupportTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ ticketId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const admin = await isPlatformAdmin(context.userId);
    const q = supabaseAdmin.from("support_tickets").select("*").eq("id", data.ticketId);
    const { data: ticket, error } = admin
      ? await q.maybeSingle()
      : await q.eq("user_id", context.userId).maybeSingle();
    if (error) throw new Error(error.message);
    if (!ticket) throw new Error("Ticket not found");
    const { data: messages } = await supabaseAdmin
      .from("support_ticket_messages")
      .select("id,sender_user_id,is_admin_reply,body,created_at")
      .eq("ticket_id", data.ticketId)
      .order("created_at", { ascending: true });
    const senderIds = Array.from(new Set((messages ?? []).map((m) => m.sender_user_id)));
    const { data: profiles } = senderIds.length
      ? await supabaseAdmin.from("profiles").select("id,email,full_name").in("id", senderIds)
      : { data: [] as any[] };
    const senders = Object.fromEntries((profiles ?? []).map((p: any) => [p.id, p]));
    return { ticket, messages: (messages ?? []).map((m) => ({ ...m, sender: senders[m.sender_user_id] ?? null })) };
  });

export const replySupportTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ticketId: z.string().uuid(), body: z.string().trim().min(1).max(5000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const admin = await isPlatformAdmin(context.userId);
    const { data: ticket } = await supabaseAdmin
      .from("support_tickets")
      .select("id,user_id,subject,category")
      .eq("id", data.ticketId)
      .maybeSingle();
    if (!ticket) throw new Error("Ticket not found");
    if (!admin && ticket.user_id !== context.userId) throw new Error("Forbidden");

    await supabaseAdmin.from("support_ticket_messages").insert({
      ticket_id: data.ticketId,
      sender_user_id: context.userId,
      is_admin_reply: admin,
      body: data.body,
    });
    await supabaseAdmin
      .from("support_tickets")
      .update({
        last_message_at: new Date().toISOString(),
        last_message_by_admin: admin,
        status: admin ? "waiting_on_user" : "open",
      })
      .eq("id", data.ticketId);

    if (!admin) {
      const { data: profile } = await supabaseAdmin
        .from("profiles")
        .select("email")
        .eq("id", context.userId)
        .maybeSingle();
      await notifyAdmins({
        isReply: true,
        subject: ticket.subject,
        category: ticket.category,
        body: data.body,
        userEmail: profile?.email ?? "(unknown)",
        ticketId: ticket.id,
      });
    }
    return { ok: true };
  });

export const adminListSupportTickets = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ status: z.enum(["all", ...STATUSES]).optional().default("all") })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    if (!(await isPlatformAdmin(context.userId))) throw new Error("Forbidden");
    let q = supabaseAdmin
      .from("support_tickets")
      .select("id,subject,category,status,user_id,created_at,last_message_at,last_message_by_admin")
      .order("last_message_at", { ascending: false })
      .limit(500);
    if (data.status !== "all") q = q.eq("status", data.status);
    const { data: tickets, error } = await q;
    if (error) throw new Error(error.message);
    const userIds = Array.from(new Set((tickets ?? []).map((t) => t.user_id)));
    const { data: profiles } = userIds.length
      ? await supabaseAdmin.from("profiles").select("id,email,full_name").in("id", userIds)
      : { data: [] as any[] };
    const userMap = Object.fromEntries((profiles ?? []).map((p: any) => [p.id, p]));
    return {
      tickets: (tickets ?? []).map((t) => ({ ...t, user: userMap[t.user_id] ?? null })),
    };
  });

export const adminUpdateTicketStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ticketId: z.string().uuid(), status: z.enum(STATUSES) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    if (!(await isPlatformAdmin(context.userId))) throw new Error("Forbidden");
    const { error } = await supabaseAdmin
      .from("support_tickets")
      .update({ status: data.status })
      .eq("id", data.ticketId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
